import { supabase } from "./client";
import { Shift, ShiftPayment, ShiftPaymentWithRelations, Notification } from "@/types/models";

export const getShiftPayments = async (shiftId: string): Promise<ShiftPayment[]> => {
  const { data, error } = await supabase
    .from('shift_payments')
    .select(`*,
      bank:bank_id (
        id,
        name,
        active
      )
    `)
    .eq('shift_id', shiftId);

    if (error) throw error;
    return data || [];
}

export const closeShift = async (shiftId: string, completedRentals: number): Promise<Shift> => {
  const { data, error } = await supabase
    .from('shifts')
    .update({
      end_time: new Date().toISOString(),
      completed_rentals: completedRentals,
      status: 'closed',
    })
    .eq('id', shiftId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const updateShiftStatus = async (shiftId: string, status: Shift['status']) => {
  const { error } = await supabase
    .from('shifts')
    .update({ status } as Shift)
    .eq('id', shiftId);

  if (error) {
    throw error;
  }
};

export const createShiftPayment = async (paymentData: {
  shift_id: string;
  bank_id: number;
  amount: number;
  reference: string;
  exchange_rate: number;
  receipt_photo_url: string;
  operator_id: string;
  branch_id: string;
}): Promise<ShiftPayment> => {
  const { data, error } = await supabase
    .from('shift_payments')
    .insert({ ...paymentData, status: 'pending' }) // Set status to pending
    .select()
    .single();

  if (error) {
    throw error;
  }

  await updateShiftStatus(paymentData.shift_id, 'in_review');

  return data;
};

export const getPendingPayments = async (branchId?: string): Promise<ShiftPaymentWithRelations[]> => {
  let query;
  if (branchId) {  
    // For branch_admin, fetch payments only for their branch using inner joins
    query = supabase
      .from('shift_payments')
      .select(`*,
        shift:shifts!inner(*, 
          operator:profiles!inner(*, 
            branch:branches!inner(*)
          )
        ),
        bank:bank_id(*)`
      )
      .eq('shift.operator.branch_id', branchId);
  } else {
    // For admin, fetch all payments
    query = supabase
      .from('shift_payments')
      .select(`*,
        shift:shifts!inner(*, 
          operator:profiles(*, 
            branch:branches(*)
          )
        ),
        bank:bank_id(*)`
      );
  }

  query = query
    .eq('status', 'pending')
    .eq('shift.is_deleted', false)
    .is('shift.deleted_at', null)
    .order('created_at', { ascending: true });

  const { data, error } = await query;

  if (error) {
    console.error("Error fetching pending payments:", error);
    throw error;
  }

  // Map the data to ensure consistent object structure, especially for potentially null relations
  const result: ShiftPaymentWithRelations[] = (data || []).map((item: any) => {
    const shift = item.shift ? {
      ...item.shift,
      operator: item.shift.operator ? {
        ...item.shift.operator,
        branch: item.shift.operator.branch || null,
      } : null,
    } : null;

    return {
      ...item,
      shift,
      bank: item.bank || null,
    };
  });

  return result.filter(item => item.shift && !item.shift.is_deleted && !item.shift.deleted_at);
};

export const updatePayment = async (
  paymentId: string,
  updates: { amount?: number; reference?: string; status: 'approved' | 'rejected'; rejection_reason?: string }
): Promise<ShiftPayment> => {
  const { data, error } = await supabase
    .from('shift_payments')
    .update(updates)
    .eq('id', paymentId);
    /*.select()
    .single();*/

  if (error) {
    throw error;
  }

  return data;
};

export const createNotification = async (notification: { user_id: string; message: string; shift_payment_id: string }) => {
  const { error } = await supabase
    .from('notifications')
    .insert(notification);

  if (error) {
    throw error;
  }
};